/**
 * **Einstellungen auf einer bestimmten Seite öffnen** (#262, „Ablage › Importieren"): was Main und
 * das Einstellungsfenster darüber austauschen. Ein frisches Fenster bekommt das Ziel in der URL, damit
 * schon das erste Bild die Seite zeigt; ein offenes bekommt es als Nachricht.
 *
 * Die Ids sind hier bloße Strings — Main kennt den Baum nicht. Der Renderer prüft sie gegen
 * `SETTINGS_TREE`, bevor er umschaltet.
 */
/**
 * Was die Seite gleich tun soll (#158, #252): eine Unterseite öffnen statt nur die Übersicht zu zeigen.
 * „Ablage › Backup wiederherstellen …“ öffnet so den Ablauf, nicht die Seite davor.
 */
export const PREFERENCES_ACTIONS = ['create-backup', 'restore-backup', 'import-todoist', 'export-csv', 'export-markdown'] as const
export type PreferencesAction = (typeof PREFERENCES_ACTIONS)[number]

export interface PreferencesTarget {
  topNavId: string
  subNavId: string
  action?: PreferencesAction
}

export function isPreferencesAction(value: unknown): value is PreferencesAction {
  return typeof value === 'string' && (PREFERENCES_ACTIONS as readonly string[]).includes(value)
}

/** Ein Ziel aus dem Renderer, nur der Form nach geprüft — Main kennt den Baum nicht. `null`: ohne Ziel öffnen. */
export function toPreferencesTarget(raw: unknown): PreferencesTarget | null {
  if (!raw || typeof raw !== 'object') return null
  const { topNavId, subNavId, action } = raw as Record<string, unknown>
  if (typeof topNavId !== 'string' || typeof subNavId !== 'string') return null
  return isPreferencesAction(action) ? { topNavId, subNavId, action } : { topNavId, subNavId }
}

export const PREFERENCES_NAVIGATE_CHANNEL = 'preferences:navigate'

const NAV_PARAM = 'nav'
const ACTION_PARAM = 'action'

/** `?nav=account-data%2Fimport&action=import-todoist` — zum Anhängen an die Fenster-URL. */
export function preferencesNavQuery(target: PreferencesTarget): string {
  const params = new URLSearchParams({ [NAV_PARAM]: `${target.topNavId}/${target.subNavId}` })
  if (target.action) params.set(ACTION_PARAM, target.action)
  return `?${params}`
}

/** `location.search` ohne die Aktion — das Ziel bleibt, damit Neu laden auf derselben Seite landet. */
export function withoutPreferencesAction(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(ACTION_PARAM)
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}

/** Liest das Ziel aus `location.search`; `null`, wenn keins oder keins in der Form `top/sub` da ist. */
export function readPreferencesNavQuery(search: string): PreferencesTarget | null {
  const params = new URLSearchParams(search)
  const raw = params.get(NAV_PARAM)
  if (!raw) return null
  const [topNavId, subNavId, ...rest] = raw.split('/')
  if (!topNavId || !subNavId || rest.length > 0) return null
  const action = params.get(ACTION_PARAM)
  return isPreferencesAction(action) ? { topNavId, subNavId, action } : { topNavId, subNavId }
}
